import React, { Component } from 'react';
import {
  BundleJson,
  BundleStatus,
  BundleStatusActive,
  CourierJson,
} from '../../types/bundleTypes';
import Fetch from 'utils/fetch';
import PlannerMapMap, { OrderMarker } from './planner-map/map/PlannerMapMap';
import PlannerMapMenu from './planner-map/menu/PlannerMapMenu';
import { COLOR } from 'const/color';
import './PlannerMap.scss';
import {
  Order,
  OrderListOrder,
  OrderListOrderClazz,
  OrderSearchStatus,
  OrderStatus,
  Region,
} from '../../types/orderTypes';
import { settings } from '../../settings';
import Button, { ButtonGroup } from '../../components/buttons/Button';
import { FaSyncAlt } from 'react-icons/fa';
import dayjs, { Dayjs } from 'dayjs';
import { getBundleColor } from './utils/bundleDraft';
import { flatten, unique } from '../../utils/array';
import { Loader } from '../../components/Loader/Loader';
import magic from '../../assets/magic.gif';
import { PlannerMenu } from './PlannerMenu';
import { getHubConfig } from '../../const/HubConfig';
import { fetchCourierByIdList } from '../../fetch/fetchUsers';
import { fetchOrderListOrdersById } from '../../fetch/orderFetch';
import { fetchBundle } from '../bundles/components/BundleUtils';
import { AutoBundlerProgressReport } from '../../utils/bundlingService';
import AutoBundlerProgress from './AutoBundlerProgress';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const BundleStatusFilter: { text: string; statusList: BundleStatus[] }[] = [
  { text: 'Draft', statusList: [BundleStatus.Draft] },
  {
    text: 'Published',
    statusList: [BundleStatus.Published, BundleStatus.Proposed],
  },
  { text: 'In Delivery', statusList: [BundleStatus.Accepted] },
];

interface PlannerMapState {
  date: Dayjs;
  allOrders: OrderListOrderClazz[];
  orders: OrderListOrderClazz[];
  allBundles: BundleJson[];
  bundles: BundleJson[];
  bundleStatuses: BundleStatus[];
  selectedOrders: OrderListOrderClazz[];
  filteredOrders: OrderListOrderClazz[];
  isLoading: boolean;
  filteredBundlesByDateOrders: string[];
  deliveryAreaBoundary?: {
    features: google.maps.Data.FeatureOptions[];
    type: string;
  };
  couriers: CourierJson[];
  autoBundlerProgress?: AutoBundlerProgressReport;
}

interface PlannerMapProps {
  region: Region;
}

class PlannerMap extends Component<PlannerMapProps, PlannerMapState> {
  constructor(props) {
    super(props);

    // TODO Blankstate?
    this.state = {
      date: dayjs(),
      allOrders: [],
      allBundles: [],
      orders: [],
      bundles: [],
      bundleStatuses: [BundleStatus.Draft],
      filteredBundlesByDateOrders: [],
      selectedOrders: [],
      filteredOrders: [],
      isLoading: true,
      couriers: [],
      autoBundlerProgress: null,
    };
  }

  async componentDidMount() {
    this.refreshState(this.state.date);
  }

  async componentDidUpdate(
    prevProps: Readonly<PlannerMapProps>,
    prevState: PlannerMapState
  ) {
    if (
      !this.state.date.isSame(prevState.date) ||
      (this.props.region && prevProps.region !== this.props.region)
    ) {
      this.refreshState(this.state.date);
    }
  }

  refreshState = async date => {
    const formattedDate = date.format('YYYY-MM-DD');
    const region = this.props.region;
    const showBundleStatuses = new Set(this.state.bundleStatuses);

    const bundleRequest = Fetch.get({
      url: `${settings.newApiUrl}/bundle?status=${BundleStatusActive.join(
        ','
      )}&region=${region}`,
    });

    const orderStatus = OrderSearchStatus.Active;
    const orderRequest = Fetch.get({
      url: `${settings.newApiUrl}/order/overview-list?start=${formattedDate}&end=${formattedDate}&status=${orderStatus}&region=${region}&includeBundleIds=true`,
    });

    const polylineRequest = Fetch.get({
      url: `${settings.newApiUrl}/availability/area-overlay/${region}`,
    });

    Promise.all([bundleRequest, orderRequest, polylineRequest])
      .then(async ([bundleResponse, orderResponse, polylineResponse]) => {
        if (!bundleResponse.ok) {
          throw Error('Could not fetch bundles');
        }
        if (!orderResponse.ok) {
          throw Error('Could not fetch orders');
        }

        return [
          await bundleResponse.json(),
          await orderResponse.json(),
          await polylineResponse.json(),
        ];
      })
      .then(async ([bundleJson, orderJson, polylineJson]) => {
        const bundles: BundleJson[] = bundleJson.items;
        const orders: OrderListOrderClazz[] = (
          orderJson as OrderListOrder[]
        ).map(order => {
          return new OrderListOrderClazz(order);
        });
        const filteredBundlesByDateOrders = Array.from(
          new Set(orders.map(order => order.bundleId))
        );

        this.updateVisibleOrdersAndBundles(
          showBundleStatuses,
          bundles,
          orders,
          filteredBundlesByDateOrders
        );
        this.fetchCouriers(bundles);
        this.setState({
          allBundles: bundles.filter(bundle =>
            filteredBundlesByDateOrders.includes(bundle.id)
          ),
          allOrders: orders,
          deliveryAreaBoundary: polylineJson,
          selectedOrders: [],
          isLoading: false,
          filteredBundlesByDateOrders,
        });
      })
      .catch(error => console.error(error.message));
  };

  async refreshBundleAndOrders(
    bundleId: string,
    orderIds: string[]
  ): Promise<void> {
    const bundleRequest = fetchBundle(bundleId);
    const orderRequest = fetchOrderListOrdersById(orderIds);

    Promise.all([bundleRequest, orderRequest])
      .then(async ([fetchedBundle, ordersJson]) => {
        // replace loaded orders in allOrders
        const fetchedOrders: OrderListOrderClazz[] = ordersJson
          .map(order => new OrderListOrderClazz(order))
          .filter(order =>
            [
              OrderStatus.Ready,
              OrderStatus.Accepted,
              OrderStatus.InDelivery,
            ].includes(order.status)
          );
        const refreshedOrders = this.state.allOrders;
        fetchedOrders.forEach(fetchedOrder => {
          const orderIndex = refreshedOrders.findIndex(
            o => o.id === fetchedOrder.id
          );
          if (orderIndex > -1) {
            refreshedOrders[orderIndex] = fetchedOrder;
          } else {
            refreshedOrders.push(fetchedOrder);
          }
        });

        // replace loaded bundle in allBundles
        const refreshedBundles = this.state.allBundles;
        const bundleIndex = refreshedBundles.findIndex(
          b => b.id === fetchedBundle.id
        );
        if (bundleIndex > -1) {
          refreshedBundles[bundleIndex] = fetchedBundle;
        } else {
          refreshedBundles.push(fetchedBundle);
        }
        const filteredBundlesByDateOrders = Array.from(
          new Set(refreshedOrders.map(order => order.bundleId))
        );
        const showBundleStatuses = new Set(this.state.bundleStatuses);
        this.updateVisibleOrdersAndBundles(
          showBundleStatuses,
          refreshedBundles,
          refreshedOrders,
          filteredBundlesByDateOrders
        );
        this.setState({
          allBundles: refreshedBundles.filter(bundle =>
            filteredBundlesByDateOrders.includes(bundle.id)
          ),
          allOrders: refreshedOrders,
          filteredBundlesByDateOrders,
        });
      })
      .catch(error => console.error(error.message));
  }

  async fetchCouriers(bundles: BundleJson[]) {
    const courierIds = unique(
      bundles
        .filter(bundle => !!bundle.assignedCourierId)
        .map(bundle => Number(bundle.assignedCourierId))
    );

    const couriersTable = await fetchCourierByIdList(courierIds);
    if (!couriersTable) {
      return;
    }
    const couriers = Object.values(couriersTable);
    this.setState({ couriers: couriers });
  }

  private updateVisibleOrdersAndBundles(
    showBundleStatuses: Set<BundleStatus>,
    allBundles: BundleJson[],
    allOrders: OrderListOrderClazz[],
    filteredBundlesByDateOrders: string[]
  ) {
    const hiddenOrderIds: Set<string> = new Set(
      flatten(
        allBundles
          .filter(bundle => !showBundleStatuses.has(bundle.status))
          .map(bundle =>
            bundle.instructions.map(instruction => instruction.orderId)
          )
      )
    );

    this.setState({
      bundles: allBundles.filter(
        bundle =>
          showBundleStatuses.has(bundle.status) &&
          filteredBundlesByDateOrders.includes(bundle.id)
      ),
      orders: allOrders.filter(order => !hiddenOrderIds.has(order.id)),
    });
  }

  updateSelectedOrders = (changedOrders: OrderListOrderClazz[]) => {
    const { selectedOrders } = this.state;
    const existingOrdersIds = new Set(selectedOrders.map(order => order.id));

    const removedOrders = new Set(
      changedOrders
        .map(order => order.id)
        .filter(orderId => existingOrdersIds.has(orderId))
    );

    const newOrders = changedOrders.filter(
      order => !existingOrdersIds.has(order.id)
    );

    const newSelectedOrders = selectedOrders
      .filter(order => !removedOrders.has(order.id))
      .concat(newOrders);

    this.setState({
      selectedOrders: newSelectedOrders,
    });
  };

  changeDate = async days => {
    const { date } = this.state;
    const newDate = date.add(days, 'day');
    this.setState({
      date: newDate,
      isLoading: true,
    });
  };

  render() {
    const {
      orders,
      allBundles,
      allOrders,
      bundles,
      couriers,
      selectedOrders,
      filteredOrders,
      date,
      deliveryAreaBoundary,
      autoBundlerProgress,
      filteredBundlesByDateOrders,
    } = this.state;
    const bundlerFailed = autoBundlerProgress?.failed;

    const bundleStatuses = new Set(this.state.bundleStatuses);
    const ordersWithVisualizationData =
      this.generateOrderMarkersWithColorAndHighlighting(
        bundles,
        filteredOrders,
        selectedOrders,
        orders
      );
    const bundlesCountByStatus = allBundles.reduce((table, bundle) => {
      const status = bundle.status;
      if (!table.has(status)) {
        table.set(status, 0);
      }
      table.set(status, table.get(status) + 1);
      return table;
    }, new Map<BundleStatus, number>());

    function bundleCount(statusList: BundleStatus[]): string {
      const value = statusList.reduce(
        (total: number, status) =>
          total + (bundlesCountByStatus.get(status) || 0),
        0
      );
      return value === 0 ? '' : ` (${value})`;
    }
    return (
      <>
        <Helmet>
          <title>Planner Map</title>
        </Helmet>
        <PlannerMenu />
        <div
          style={{
            backgroundColor: 'orange',
            height: 30,
            textAlign: 'center',
            color: 'black',
            fontSize: 20,
            fontWeight: 'bold',
          }}
        >
          <Link to="/planner">Switch back to the new planner</Link>
        </div>
        <div className="planner-map">
          {this.state.isLoading && (
            <div className="planner-map-loader">
              <div className="planner-map-loader-title">
                <Loader size="large" />
                &nbsp; Creating magic...
                <br />
                <img src={magic} alt="From all of us to all of you..." />
              </div>
            </div>
          )}
          <div className="planner-map__filter-menu">
            <div className="filter-menu__header">
              <div
                style={{
                  marginBottom: 5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  flexGrow: 2,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    flexGrow: 2,
                  }}
                >
                  <Button
                    title={<FaSyncAlt />}
                    onClick={() => this.refreshState(date)}
                  />
                  {bundlerFailed && (
                    <div className="filter-menu__header-autobundler">
                      <span>Auto bundling failed</span>
                      <div className="filter-menu__header__progress-bar-wrapper">
                        <div className="filter-menu__header__progress-bar">
                          <div
                            className="filter-menu__header__progress-bar-fill"
                            style={{
                              width: `100%`,
                            }}
                          >
                            <span
                              className="filter-menu__header__progress-bar-fill_animate filter-menu__header__progress-bar-fill_progress"
                              style={{
                                backgroundColor: '#D8000C',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {autoBundlerProgress && !bundlerFailed && (
                    <AutoBundlerProgress
                      autoBundlerProgress={autoBundlerProgress}
                    />
                  )}
                </div>
                <ButtonGroup>
                  <Button title="<" onClick={() => this.changeDate(-1)} />
                  <Button title={date.format('ddd D. MMM')} />
                  <Button title=">" onClick={() => this.changeDate(1)} />
                </ButtonGroup>
              </div>
              {
                <div style={{ marginBottom: 10 }}>
                  <div className="planner-map__filter-status">
                    <span>Bundle status:</span>
                    {BundleStatusFilter.map(statusFilter => {
                      const statusId = `bundleStatus_${statusFilter.text
                        .toLowerCase()
                        .replace(' ', '_')}`;
                      return (
                        <label htmlFor={statusId} key={statusId}>
                          <input
                            type="checkbox"
                            id={statusId}
                            checked={statusFilter.statusList.some(
                              bundleStatus => bundleStatuses.has(bundleStatus)
                            )}
                            onChange={event => {
                              if (event.target.checked) {
                                statusFilter.statusList.forEach(bundleStatus =>
                                  bundleStatuses.add(bundleStatus)
                                );
                              } else {
                                statusFilter.statusList.forEach(bundleStatus =>
                                  bundleStatuses.delete(bundleStatus)
                                );
                              }
                              this.setState(
                                {
                                  bundleStatuses: Array.from(bundleStatuses),
                                },
                                () =>
                                  this.updateVisibleOrdersAndBundles(
                                    bundleStatuses,
                                    allBundles,
                                    allOrders,
                                    filteredBundlesByDateOrders
                                  )
                              );
                            }}
                          />{' '}
                          <span>
                            {`${statusFilter.text}${bundleCount(
                              statusFilter.statusList
                            )}`}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              }
            </div>
            <PlannerMapMenu
              middleText={`All unfiltered orders: ${allOrders.length}`}
              date={date}
              orders={orders}
              bundles={bundles}
              couriers={couriers}
              filteredOrders={filteredOrders}
              selectedOrders={selectedOrders}
              onFetchAll={this.refreshState}
              onOrdersRemoved={(bundleId: string, orderIds: string[]) =>
                this.refreshBundleAndOrders(bundleId, orderIds)
              }
              onFilteredOrders={orders => {
                this.setState({ filteredOrders: orders });
              }}
              onOrderDeselect={orderIds => {
                const newList = selectedOrders;
                orderIds.forEach(orderId => {
                  const existingIndex = selectedOrders.findIndex(
                    value => value && value.id === orderId
                  );
                  delete newList[existingIndex];
                });
                this.setState({
                  selectedOrders: newList.filter(value => value != null),
                });
              }}
              onAutoBundlerProgress={progress => {
                this.setState({ autoBundlerProgress: progress });
              }}
              region={this.props.region}
            />
          </div>

          <PlannerMapMap
            region={this.props.region}
            orders={ordersWithVisualizationData}
            updateSelectedOrders={this.updateSelectedOrders}
            deliveryAreaBoundary={deliveryAreaBoundary}
            hubPositions={[getHubConfig(this.props.region).hubAddress.location]}
            onFetchAll={this.refreshState}
            date={date}
            selectedOrders={selectedOrders}
          />
        </div>
      </>
    );
  }

  private generateOrderMarkersWithColorAndHighlighting(
    bundles: BundleJson[],
    filteredOrders: OrderListOrderClazz[],
    selectedOrders: OrderListOrderClazz[],
    orders: OrderListOrderClazz[]
  ): OrderMarker[] {
    const ordersMap: Map<string, OrderListOrderClazz> = orders.reduce(
      (map, order) => {
        map.set(order.id, order);
        return map;
      },
      new Map()
    );
    const selectedOrdersSet = new Set(selectedOrders.map(order => order.id));
    const filteredOrdersSet = new Set(filteredOrders.map(order => order.id));

    const selectedMarkers: OrderMarker[] = selectedOrders.map(order => {
      return {
        order: order,
        group: order.windowTime(),
        color: COLOR.RED,
        highlighted: true,
        bold: false,
        clickable: true,
        selected: true,
        rightClickable: true,
      };
    });

    const bundledMarkers: OrderMarker[] = flatten(
      bundles.map(currentBundle => {
        const bundleColor = getBundleColor(currentBundle.id);

        const ordersInBundles = Array.from(
          new Set(
            currentBundle.instructions.map(instruction => instruction.orderId)
          )
        )
          .filter(orderId => ordersMap.has(orderId))
          .map(orderId => ordersMap.get(orderId));

        return ordersInBundles
          .filter(order => !selectedOrdersSet.has(order.id))
          .map(order => {
            return {
              order: order,
              group: order.windowTime(),
              color: bundleColor,
              highlighted: true,
              bold: false,
              clickable: false,
              selected: false,
              rightClickable: true,
              bundleId: currentBundle.id,
            };
          });
      })
    );

    const bundleOrders = new Set(
      bundledMarkers.map(orderMarker => orderMarker.order.id)
    );
    const unBundledMarkers: OrderMarker[] = orders
      .filter(
        order => !bundleOrders.has(order.id) && !selectedOrdersSet.has(order.id)
      )
      .map(order => {
        return {
          order: order,
          group: order.windowTime(),
          color: 'rgba(242,243,244, 1)',
          highlighted: true,
          bold: false,
          clickable: true,
          selected: false,
          rightClickable: true,
        };
      });

    return bundledMarkers
      .concat(unBundledMarkers)
      .concat(selectedMarkers)
      .map(marker => {
        // Set opacity if outside filtered orders
        if (!filteredOrdersSet.has(marker.order.id)) {
          return {
            ...marker,
            highlighted: false,
            clickable: false,
            rightClickable: false,
          };
        }
        return marker;
      });
  }
}

export function filterNotInBundles<T extends Order | OrderListOrder>(
  orders: T[],
  bundles: BundleJson[]
): T[] {
  const orderIds: Set<string> = new Set(
    flatten(
      bundles.map(bundle =>
        bundle.instructions.map(instruction => instruction.orderId)
      )
    )
  );
  return orders.filter(order => !orderIds.has(order.id));
}

export default PlannerMap;
